import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  fetchStudentsForRemarks,
  createBulkRemarks,
  getRemarkById,
  updateRemark,
} from '../api/remarksApi'
import api from '../../../api/client'
import { appToast } from '../../../components/AppToast'

export default function useRemarksEntry() {
  const { id } = useParams()
  const isEditMode = Boolean(id)
  const navigate = useNavigate()

  const [remarkDate, setRemarkDate] = useState(() => new Date().toISOString().split('T')[0])
  const [remarkName, setRemarkName] = useState('Appreciation')
  const [remarkedBy, setRemarkedBy] = useState('')
  const [facultyOptions, setFacultyOptions] = useState([])

  useEffect(() => {
    async function loadFaculties() {
      try {
        const { data } = await api.get('/options/acheads', { params: { group_id: 33, page: 1, page_size: 100 } })
        const items = data?.items || []
        const mapped = items.map((f) => ({
          value: f.name || f.label || String(f.id),
          label: f.name || f.label,
        }))
        if (mapped.length > 0) {
          setFacultyOptions(mapped)
          setRemarkedBy((prev) => prev || mapped[0].value)
        }
      } catch (err) {
        console.error('Failed to load faculties from /options/acheads', err)
      }
    }
    loadFaculties()
  }, [])
  const [particulars, setParticulars] = useState('')

  const [selectedStudentIds, setSelectedStudentIds] = useState([])
  const [students, setStudents] = useState([])
  const [editingStudent, setEditingStudent] = useState(null)
  const [studentSearchText, setStudentSearchText] = useState('')
  const [studentSearchField, setStudentSearchField] = useState('All')
  const [isSaving, setIsSaving] = useState(false)
  const [loading, setLoading] = useState(false)

  // Scope Filters for student selector
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('2026-2027')
  const [batchFilter, setBatchFilter] = useState('All')
  const [subjectFilter, setSubjectFilter] = useState('All')

  // Load Students for Bulk Picker
  const loadStudents = useCallback(async () => {
    try {
      const list = await fetchStudentsForRemarks({
        company: companyFilter,
        branch: branchFilter,
        course: courseFilter,
        academicYear: academicYearFilter,
        batch: batchFilter,
        subject: subjectFilter,
        searchText: studentSearchText,
        searchField: studentSearchField,
      })
      setStudents(list)
    } catch (e) {
      console.error('Failed to load students for remarks', e)
    }
  }, [
    companyFilter,
    branchFilter,
    courseFilter,
    academicYearFilter,
    batchFilter,
    subjectFilter,
    studentSearchText,
    studentSearchField,
  ])

  useEffect(() => {
    if (!isEditMode) {
      loadStudents()
    }
  }, [loadStudents, isEditMode])

  // Load single remark in Edit Mode
  useEffect(() => {
    if (isEditMode) {
      setLoading(true)
      getRemarkById(id)
        .then((data) => {
          setRemarkDate(data.remarkDate || new Date().toISOString().split('T')[0])
          setRemarkName(data.remarkName || 'Appreciation')
          setRemarkedBy(data.remarkedBy || 'Prof. S. K. Mehta')
          setParticulars(data.particulars || '')
          setEditingStudent({
            studentId: data.studentId,
            fullName: data.fullName,
            rollNo: data.rollNo,
            courseName: data.courseName,
            batchNames: data.batchNames,
            branchName: data.branchName,
            instituteName: data.instituteName,
          })
        })
        .catch((err) => {
          console.error(err)
          appToast.error('Remark record not found.')
          navigate('/remarks/list')
        })
        .finally(() => setLoading(false))
    }
  }, [id, isEditMode, navigate])

  const handleToggleStudent = (sId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(sId) ? prev.filter((item) => item !== sId) : [...prev, sId]
    )
  }

  const handleToggleAll = () => {
    if (selectedStudentIds.length === students.length) {
      setSelectedStudentIds([])
    } else {
      setSelectedStudentIds(students.map((s) => s.id))
    }
  }

  const handleResetScopeFilters = () => {
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setAcademicYearFilter('2026-2027')
    setBatchFilter('All')
    setSubjectFilter('All')
    setStudentSearchText('')
    setStudentSearchField('All')
    appToast.info('Student filters have been reset.')
  }

  const handleSave = async (sendAlert = false) => {
    if (!remarkDate) {
      appToast.error('Please specify a valid remark date.')
      return
    }

    if (!isEditMode && selectedStudentIds.length === 0) {
      appToast.error('Please select at least one student to tag this remark.')
      return
    }

    setIsSaving(true)
    try {
      if (isEditMode) {
        await updateRemark(id, {
          remarkDate,
          remarkName,
          remarkedBy,
          particulars,
        })
        appToast.success('Remark updated successfully.')
      } else {
        await createBulkRemarks({
          studentIds: selectedStudentIds,
          remarkDate,
          remarkName,
          remarkedBy,
          particulars,
        })
        if (sendAlert) {
          appToast.success(
            `Remark saved & notifications dispatched to parents of ${selectedStudentIds.length} students.`
          )
        } else {
          appToast.success(`Remarks created successfully for ${selectedStudentIds.length} students.`)
        }
      }
      navigate('/remarks/list')
    } catch (e) {
      console.error('Failed to save remark', e)
      appToast.error('Failed to save remark.')
    } finally {
      setIsSaving(false)
    }
  }

  const options = {
    searchFields: [
      { value: 'All', label: 'All Fields' },
      { value: 'Student', label: 'Student Name' },
      { value: 'RollNo', label: 'Roll Number' },
      { value: 'IdCardNo', label: 'ID Card No' },
      { value: 'Course', label: 'Course' },
      { value: 'Batch', label: 'Batch' },
    ],
    companies: [
      { value: 'All', label: 'All Companies' },
      { value: 'Tez Educational Trust', label: 'Tez Educational Trust' },
      { value: 'Tez Educational School', label: 'Tez Educational School' },
      { value: 'Tolani College Of Commerce', label: 'Tolani College Of Commerce' },
    ],
    branches: [
      { value: 'All', label: 'All Branches' },
      { value: 'Dadar', label: 'Dadar' },
      { value: 'Thane', label: 'Thane' },
      { value: 'Andheri', label: 'Andheri' },
      { value: 'Borivali', label: 'Borivali' },
    ],
    courses: [
      { value: 'All', label: 'All Courses' },
      { value: 'CA Foundation', label: 'CA Foundation' },
      { value: 'CS Executive', label: 'CS Executive' },
      { value: 'JEE Advanced', label: 'JEE Advanced' },
      { value: 'NEET UG', label: 'NEET UG' },
    ],
    batches: [
      { value: 'All', label: 'All Batches' },
      { value: 'Batch-A (Morning)', label: 'Batch-A (Morning)' },
      { value: 'Batch-CS-01', label: 'Batch-CS-01' },
      { value: 'Super-30 (Intensive)', label: 'Super-30 (Intensive)' },
      { value: 'Med-Achievers', label: 'Med-Achievers' },
    ],
    subjects: [
      { value: 'All', label: 'All Subjects' },
      { value: 'Principles and Practice of Accounting', label: 'Accounting' },
      { value: 'Company Law & Practice', label: 'Company Law' },
      { value: 'Physics', label: 'Physics' },
      { value: 'Mathematics', label: 'Mathematics' },
      { value: 'Biology (Botany)', label: 'Biology' },
      { value: 'Chemistry', label: 'Chemistry' },
    ],
    academicYears: [
      { value: 'All', label: 'All Years' },
      { value: '2026-2027', label: '2026-2027' },
      { value: '2025-2026', label: '2025-2026' },
    ],
    remarkTypes: [
      { value: 'Appreciation', label: 'Appreciation & Praise' },
      { value: 'Late Coming', label: 'Late Coming' },
      { value: 'Homework Incomplete', label: 'Homework Incomplete' },
      { value: 'Disciplinary Warning', label: 'Disciplinary Warning' },
      { value: 'Performance Improvement', label: 'Performance Improvement' },
      { value: 'Parent Meeting Required', label: 'Parent Meeting Required' },
    ],
    facultyList: facultyOptions.length > 0
      ? facultyOptions
      : [{ value: 'Admin Office', label: 'Admin Office / Staff' }],
  }

  return {
    state: {
      isEditMode,
      loading,
      isSaving,
      remarkDate,
      remarkName,
      remarkedBy,
      particulars,
      selectedStudentIds,
      students,
      editingStudent,
      studentSearchText,
      studentSearchField,
      companyFilter,
      branchFilter,
      courseFilter,
      academicYearFilter,
      batchFilter,
      subjectFilter,
      options,
    },
    actions: {
      setRemarkDate,
      setRemarkName,
      setRemarkedBy,
      setParticulars,
      setStudentSearchText,
      setStudentSearchField,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setAcademicYearFilter,
      setBatchFilter,
      setSubjectFilter,
      handleToggleStudent,
      handleToggleAll,
      handleResetScopeFilters,
      handleSave,
    },
  }
}
